import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CalendarDays, Tag } from "lucide-react";
import { api, fullUrl } from "../lib/api";
import { setMeta } from "../lib/seo";
import { useSettings } from "../hooks/useSettings";
import { LogoBadge } from "../components/site/LogoBadge";
import { FloatingHUD } from "../components/site/FloatingHUD";

export default function PostPage() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const settings = useSettings();

    useEffect(() => {
        api.get(`/posts/${slug}`)
            .then((r) => setPost(r.data))
            .catch(() => setNotFound(true));
    }, [slug]);

    useEffect(() => {
        if (!post) return;
        document.title = post.meta_title || `${post.title} | شركة إطار الثريا لخدمات السيارات`;
        setMeta("description", post.meta_desc || post.excerpt);
        const siteKw = (settings?.page_keywords?.home || []).join(", ");
        setMeta("keywords", post.meta_keywords || (post.tags || []).join(", ") || siteKw);
        setMeta("og:title", post.title);
        setMeta("og:description", post.meta_desc || post.excerpt);
        setMeta("og:type", "article");
    }, [post, settings]);

    return (
        <div className="min-h-screen bg-[#0A1228] grain-overlay" data-testid="post-page">
            <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A1228]/85 border-b border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
                    <LogoBadge />
                    <Link
                        to="/blog"
                        data-testid="post-back-blog"
                        className="flex items-center gap-2 text-sm font-bold text-slate-300 border border-white/15 rounded-full px-5 py-2.5 hover:border-amber-400/50 hover:text-amber-400 transition-colors duration-300"
                    >
                        <ArrowRight className="w-4 h-4" />
                        عودة للمدونة
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
                {notFound ? (
                    <p className="text-center text-slate-500 py-24" data-testid="post-not-found">
                        المقال غير موجود
                    </p>
                ) : !post ? (
                    <p className="text-center text-slate-500 py-24">جاري التحميل...</p>
                ) : (
                    <article data-testid={`post-article-${post.slug}`}>
                        {post.category && (
                            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-400 bg-amber-400/10 border border-amber-400/25 rounded-full px-4 py-1.5 mb-5">
                                <Tag className="w-3.5 h-3.5" />
                                {post.category}
                            </span>
                        )}
                        <h1 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.25] mb-4">
                            {post.title}
                        </h1>
                        <p className="flex items-center gap-2 text-xs text-slate-500 mb-8" dir="ltr">
                            <CalendarDays className="w-3.5 h-3.5" />
                            {new Date(post.created_at).toLocaleDateString("ar-SA", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                            })}
                        </p>
                        {post.image && (
                            <div className="relative mb-10">
                                <img
                                    src={fullUrl(post.image)}
                                    alt={post.title}
                                    className="w-full rounded-3xl border border-white/10 max-h-[420px] object-cover"
                                />
                                <span className="absolute bottom-4 left-4 bg-white/95 rounded-xl px-2 py-1 shadow-xl" data-testid="post-hero-watermark">
                                    <img src="/assets/brand/mero-logo.jpg" alt="Mero" className="h-7 w-auto object-contain" />
                                </span>
                            </div>
                        )}
                        <div className="text-slate-300 text-base sm:text-lg leading-loose whitespace-pre-wrap">
                            {post.body}
                        </div>
                    </article>
                )}
            </main>
            <FloatingHUD />
        </div>
    );
}
